// Evidence annotator utilities (T-108).
//
// Helpers for attaching Evidence to GraphEdge instances and for working with
// evidence arrays. All operations are immutable: inputs are never mutated and
// fresh objects/arrays are returned. This keeps annotation safe to use across
// the deterministic-first pipeline where edges may be shared between modules.

import type { GraphEdge } from "../types.js";
import type { Evidence } from "./types.js";

/**
 * Returns a new edge with `evidence` appended to its evidence array.
 * The input edge and its evidence array are left untouched.
 */
export function annotateEdge(edge: GraphEdge, evidence: Evidence): GraphEdge {
  return {
    ...edge,
    evidence: [...edge.evidence, evidence],
  };
}

/**
 * Returns a new array in which every edge has been annotated with `evidence`.
 * Each edge is replaced by a fresh annotated copy (see {@link annotateEdge}).
 */
export function annotateEdges(
  edges: GraphEdge[],
  evidence: Evidence,
): GraphEdge[] {
  return edges.map((edge) => annotateEdge(edge, evidence));
}

/**
 * Composite key used to deduplicate evidence. Two evidence entries are treated
 * as the same fact when they share both `type` and `detail`.
 */
function evidenceKey(evidence: Evidence): string {
  return `${evidence.type} ${evidence.detail}`;
}

/**
 * Merges two evidence arrays, deduplicating by the `(type + detail)` key.
 * `existing` entries take precedence; an `incoming` entry with a key already
 * present in `existing` is dropped. Order is preserved: existing entries first,
 * then the surviving incoming entries.
 */
export function mergeEvidence(
  existing: Evidence[],
  incoming: Evidence[],
): Evidence[] {
  const seen = new Set<string>();
  const merged: Evidence[] = [];

  for (const evidence of existing) {
    const key = evidenceKey(evidence);
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(evidence);
    }
  }

  for (const evidence of incoming) {
    const key = evidenceKey(evidence);
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(evidence);
    }
  }

  return merged;
}

/**
 * Returns the evidence entry with the highest confidence, or `undefined` when
 * the array is empty. On ties, the first such entry encountered wins.
 */
export function bestEvidence(evidences: Evidence[]): Evidence | undefined {
  let best: Evidence | undefined;
  for (const evidence of evidences) {
    if (best === undefined || evidence.confidence > best.confidence) {
      best = evidence;
    }
  }
  return best;
}

/**
 * Returns the evidence entries whose confidence is strictly greater than
 * `threshold`.
 */
export function filterByConfidence(
  evidences: Evidence[],
  threshold: number,
): Evidence[] {
  return evidences.filter((evidence) => evidence.confidence > threshold);
}
